import Select from "react-select";
import useReports from "../../custome_hooks/reports/useReports";
import Loading from "../../utils/Loading";
import ReportLoding from "./ReportLoading";
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiCalendar,
  FiFilter,
  FiAlertTriangle,
} from "react-icons/fi";
import { TbReport } from "react-icons/tb";
import { MdOutlineSensors } from "react-icons/md";
import { useEffect, useState } from "react";
import { isOpenLoop, formatResistance } from "../../utils/sensorStatus";

/* ── Shared glassmorphic panel style ──────────────────────────── */
const glassPanel = {
  background: "rgba(13,21,38,0.75)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.07)",
  boxShadow:
    "0 8px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
};

const Report = () => {
  const {
    callReportApi,
    options,
    handleChange,
    startTime,
    handleStartTimeChange,
    endTime,
    handleEndTimeChange,
    isCriticalReport,
    setIsCriticalReport,
    reportError,
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    downloadReport,
    sensorListData,
  } = useReports();

  const {
    mutate: reportApiCall,
    data: reportApiData,
    isPending: reportLoading,
  } = callReportApi();
  const { mutate: downloadReportMutate, isPending: downloadReportLoading } =
    downloadReport();

  const [hasAutoFetched, setHasAutoFetched] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<any>(null);

  /* ── Status helpers ──────────────────────────────────────────── */
  const getReportRowStatus = (item: any) => {
    if (item?.status) {
      const s = String(item.status).toLowerCase();
      if (s === "critical") return "Critical";
      if (s === "inactive") return "Inactive";
      if (s === "normal" || s === "active") return "Active";
    }
    const rVal = item?.R ?? item?.r ?? item?.resistance;
    if (isOpenLoop(rVal)) return "Critical";
    if (rVal == null || (!isNaN(Number(rVal)) && Number(rVal) >= 99)) return "Inactive";
    const sensor = sensorListData?.content?.find(
      (s: any) => s.name === item?.deviceId
    );
    const threshold =
      sensor?.criticalThreshold != null
        ? Number(sensor.criticalThreshold)
        : 3.0;
    if (!isNaN(Number(rVal)) && Number(rVal) >= threshold) return "Critical";
    return "Active";
  };

  const statusConfig: Record<string, { bg: string; border: string; color: string; label: string }> = {
    Active: {
      bg: "rgba(16,185,129,0.10)",
      border: "rgba(16,185,129,0.25)",
      color: "#34d399",
      label: "Active",
    },
    Critical: {
      bg: "rgba(245,158,11,0.10)",
      border: "rgba(245,158,11,0.25)",
      color: "#fbbf24",
      label: "Critical",
    },
    Inactive: {
      bg: "rgba(239,68,68,0.10)",
      border: "rgba(239,68,68,0.20)",
      color: "#f87171",
      label: "Inactive",
    },
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const cfg = statusConfig[status] ?? statusConfig["Inactive"];
    return (
      <span
        className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
        style={{
          background: cfg.bg,
          border: `1px solid ${cfg.border}`,
          color: cfg.color,
        }}
      >
        {cfg.label}
      </span>
    );
  };

  /* ── Auto-select first sensor & default dates ────────────────── */
  useEffect(() => {
    if (options && options.length > 0 && options[0].value !== "" && !hasAutoFetched) {
      const firstOption = options[0];
      setSelectedSensor(firstOption);
      handleChange(firstOption);

      const now = new Date();
      const oneWeekAgo = new Date(now);
      oneWeekAgo.setDate(now.getDate() - 7);

      const fmt = (d: Date) => {
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };

      handleStartTimeChange({ target: { value: fmt(oneWeekAgo) } });
      handleEndTimeChange({ target: { value: fmt(now) } });
      setHasAutoFetched(true);
    }
  }, [options, hasAutoFetched]);

  useEffect(() => {
    if (hasAutoFetched && startTime && endTime && !reportApiData) {
      setTimeout(() => reportApiCall(), 500);
    }
  }, [hasAutoFetched, startTime, endTime]);

  useEffect(() => {
    if (hasAutoFetched && reportApiData) reportApiCall();
  }, [currentPage, pageSize]);

  const handleSensorChange = (selectedOption: any) => {
    setSelectedSensor(selectedOption);
    handleChange(selectedOption);
  };

  /* ── react-select premium dark styles ────────────────────────── */
  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "rgba(255,255,255,0.04)",
      borderColor: state.isFocused
        ? "rgba(99,102,241,0.5)"
        : "rgba(255,255,255,0.08)",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(99,102,241,0.12)"
        : "none",
      borderRadius: "12px",
      padding: "2px 4px",
      color: "white",
      transition: "all 0.2s",
      "&:hover": { borderColor: "rgba(255,255,255,0.15)" },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "rgba(13,21,38,0.98)",
      backdropFilter: "blur(20px)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "12px",
      boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
      overflow: "hidden",
    }),
    menuList: (base: any) => ({
      ...base,
      padding: "6px",
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "rgba(99,102,241,0.20)"
        : state.isFocused
        ? "rgba(255,255,255,0.06)"
        : "transparent",
      color: state.isSelected ? "#a5b4fc" : "white",
      borderRadius: "8px",
      fontSize: "13px",
      padding: "8px 12px",
      cursor: "pointer",
      transition: "background 0.15s",
    }),
    singleValue: (base: any) => ({ ...base, color: "white", fontSize: "13px" }),
    input: (base: any) => ({ ...base, color: "white", fontSize: "13px" }),
    placeholder: (base: any) => ({ ...base, color: "#64748b", fontSize: "13px" }),
    dropdownIndicator: (base: any) => ({ ...base, color: "#475569", "&:hover": { color: "#94a3b8" } }),
    clearIndicator: (base: any) => ({ ...base, color: "#475569" }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  /* ── Shared datetime input style ─────────────────────────────── */
  const dtInputStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "12px",
    color: "white",
    colorScheme: "dark" as any,
    width: "100%",
    padding: "10px 14px",
    fontSize: "13px",
    outline: "none",
    transition: "all 0.2s",
  };

  const hasData =
    !reportError && reportApiData?.data?.content?.length > 0;

  return (
    <div
      className="flex flex-col min-h-[calc(100vh-64px)] p-3 sm:p-5 lg:p-6 text-white"
      style={{
        background:
          "linear-gradient(160deg, #070c18 0%, #0a0f1e 60%, #08101f 100%)",
      }}
    >
      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3.5">
          <div
            className="p-2.5 rounded-xl flex-shrink-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(59,130,246,0.12))",
              border: "1px solid rgba(99,102,241,0.25)",
              boxShadow: "0 4px 16px rgba(99,102,241,0.15)",
            }}
          >
            <TbReport className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              Reports
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
              Generate and download earth pit telemetry reports
            </p>
          </div>
        </div>

        {/* Quick stats from loaded data */}
        {hasData && (
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{
                background: "rgba(99,102,241,0.10)",
                border: "1px solid rgba(99,102,241,0.20)",
                color: "#818cf8",
              }}
            >
              {reportApiData?.data?.totalElements ?? reportApiData?.data?.content?.length} records
            </div>
            <div
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{
                background: "rgba(59,130,246,0.10)",
                border: "1px solid rgba(59,130,246,0.20)",
                color: "#60a5fa",
              }}
            >
              Page {currentPage + 1} / {reportApiData?.data?.totalPages}
            </div>
          </div>
        )}
      </div>

      {/* ── 2-panel content ─────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-4 lg:flex-1 lg:min-h-0 lg:h-[calc(100vh-196px)]">

        {/* ── LEFT: Filter sidebar ─────────────────────────────── */}
        <aside
          className="w-full lg:w-80 xl:w-[340px] flex-shrink-0 rounded-2xl flex flex-col"
          style={glassPanel}
        >
          {/* Sidebar header */}
          <div
            className="flex items-center gap-2.5 px-5 py-4 flex-shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div
              className="p-1.5 rounded-lg"
              style={{
                background: "rgba(99,102,241,0.12)",
                border: "1px solid rgba(99,102,241,0.20)",
              }}
            >
              <FiFilter className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Report Filters</h2>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Configure and generate telemetry data
              </p>
            </div>
          </div>

          {/* Filter fields */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-5 space-y-4">

            {/* Earth Pit selector */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <MdOutlineSensors className="w-3.5 h-3.5 text-indigo-400" />
                Earth Pit
              </label>
              <Select
                options={options}
                value={selectedSensor}
                onChange={handleSensorChange}
                placeholder="Choose a sensor…"
                isSearchable
                styles={selectStyles}
                className="text-sm"
              />
            </div>

            {/* Start time */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <FiCalendar className="w-3.5 h-3.5 text-indigo-400" />
                Start Time
              </label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={handleStartTimeChange}
                max={new Date().toISOString().slice(0, 16)}
                style={dtInputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = "rgba(99,102,241,0.5)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255,255,255,0.08)";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* End time */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <FiCalendar className="w-3.5 h-3.5 text-indigo-400" />
                End Time
              </label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={handleEndTimeChange}
                max={new Date().toISOString().slice(0, 16)}
                style={dtInputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = "rgba(99,102,241,0.5)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255,255,255,0.08)";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Critical report toggle */}
            <div
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl cursor-pointer transition-all select-none"
              style={{
                background: isCriticalReport
                  ? "rgba(245,158,11,0.08)"
                  : "rgba(255,255,255,0.03)",
                border: isCriticalReport
                  ? "1px solid rgba(245,158,11,0.25)"
                  : "1px solid rgba(255,255,255,0.07)",
              }}
              onClick={() => setIsCriticalReport(!isCriticalReport)}
            >
              <div
                className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-all"
                style={{
                  background: isCriticalReport
                    ? "rgba(245,158,11,0.90)"
                    : "rgba(255,255,255,0.06)",
                  border: isCriticalReport
                    ? "1px solid rgba(245,158,11,0.8)"
                    : "1px solid rgba(255,255,255,0.15)",
                }}
              >
                {isCriticalReport && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <FiAlertTriangle
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{ color: isCriticalReport ? "#fbbf24" : "#64748b" }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: isCriticalReport ? "#fde68a" : "#94a3b8" }}
                  >
                    Critical Report Only
                  </span>
                </div>
                <p className="text-[10px] text-slate-600 mt-0.5">
                  Filter to show only critical readings
                </p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div
            className="p-4 sm:p-5 space-y-2.5 flex-shrink-0"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            <button
              onClick={() => reportApiCall()}
              disabled={reportLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #4f46e5, #3b82f6)",
                boxShadow: "0 4px 16px rgba(79,70,229,0.35)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                  "0 6px 24px rgba(79,70,229,0.5)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                  "0 4px 16px rgba(79,70,229,0.35)";
              }}
            >
              {reportLoading ? (
                <Loading />
              ) : (
                <>
                  <TbReport className="w-4 h-4" />
                  Fetch Report
                </>
              )}
            </button>

            <button
              onClick={() => downloadReportMutate()}
              disabled={downloadReportLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#94a3b8",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "rgba(255,255,255,0.08)";
                (e.currentTarget as HTMLButtonElement).style.color = "#e2e8f0";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "rgba(255,255,255,0.04)";
                (e.currentTarget as HTMLButtonElement).style.color = "#94a3b8";
              }}
            >
              {downloadReportLoading ? (
                <Loading />
              ) : (
                <>
                  <FiDownload className="w-4 h-4" />
                  Download CSV
                </>
              )}
            </button>
          </div>
        </aside>

        {/* ── RIGHT: Report table panel ────────────────────────── */}
        <main
          className="flex-1 rounded-2xl flex flex-col overflow-hidden min-h-[500px] lg:min-h-0"
          style={glassPanel}
        >
          {/* ── Empty / Error state ──────────────────────────── */}
          {(reportError || (!hasData && !reportLoading)) && (
            <div className="flex flex-col items-center justify-center flex-1 px-6 py-16 text-center">
              <div
                className="p-5 rounded-2xl mb-5"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <TbReport className="w-12 h-12 text-slate-600" />
              </div>
              <p className="text-base font-semibold text-slate-400 mb-1">
                {reportError ? "No Sensor Data Available" : "No Data Found"}
              </p>
              <p className="text-sm text-slate-600 max-w-xs">
                {reportError
                  ? "Please select a sensor and time range to generate a report"
                  : "Select filters on the left and click \"Fetch Report\""}
              </p>
            </div>
          )}

          {/* ── Data table ──────────────────────────────────────── */}
          {hasData && (
            <div className="flex flex-col h-full">

              {/* Table toolbar */}
              <div
                className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-3.5 gap-3 flex-shrink-0"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
              >
                {/* Title + count */}
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
                  </span>
                  <h2 className="text-sm sm:text-base font-semibold text-white">
                    Report Data
                  </h2>
                </div>

                {/* Pagination + rows per page */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Rows per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500 font-medium whitespace-nowrap">
                      Rows
                    </span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="text-xs px-2.5 py-1.5 rounded-lg text-white focus:outline-none cursor-pointer"
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <option value="100" style={{ background: "#0d1526" }}>100</option>
                      <option value="150" style={{ background: "#0d1526" }}>150</option>
                      <option value="200" style={{ background: "#0d1526" }}>200</option>
                    </select>
                  </div>

                  {/* Pagination controls */}
                  <div
                    className="flex items-center gap-1 rounded-xl overflow-hidden"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <button
                      onClick={() => setCurrentPage((p) => (p > 0 ? p - 1 : 0))}
                      disabled={reportApiData?.data?.first}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.06] text-slate-300 cursor-pointer"
                    >
                      <FiChevronLeft className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Prev</span>
                    </button>
                    <span
                      className="px-3 py-1.5 text-xs font-semibold text-indigo-300 tabular-nums"
                      style={{ borderLeft: "1px solid rgba(255,255,255,0.06)", borderRight: "1px solid rgba(255,255,255,0.06)" }}
                    >
                      {currentPage + 1} / {reportApiData?.data?.totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={reportApiData?.data?.last}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.06] text-slate-300 cursor-pointer"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <FiChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Scrollable table */}
              <div className="flex-1 overflow-auto custom-scrollbar" style={{ minHeight: "320px" }}>
                <table className="w-full border-collapse" style={{ minWidth: "600px" }}>
                  <thead className="sticky top-0 z-10">
                    <tr
                      style={{
                        background: "rgba(10,15,30,0.95)",
                        backdropFilter: "blur(12px)",
                        borderBottom: "1px solid rgba(99,102,241,0.15)",
                      }}
                    >
                      {["#", "Device", "Date", "Time", "Resistance (Ω)", "Voltage (V)", "Current (A)", "Status"].map(
                        (col) => (
                          <th
                            key={col}
                            className="px-3 sm:px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest"
                            style={{ color: "#6366f1" }}
                          >
                            {col}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {reportLoading ? (
                      <ReportLoding pageSize={pageSize} />
                    ) : (
                      reportApiData?.data?.content.map((item: any, index: number) => {
                        const status = getReportRowStatus(item);
                        const isEven = index % 2 === 0;
                        return (
                          <tr
                            key={index}
                            className="transition-colors group"
                            style={{
                              background: isEven
                                ? "rgba(255,255,255,0.01)"
                                : "transparent",
                              borderBottom: "1px solid rgba(255,255,255,0.04)",
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLTableRowElement).style.background =
                                "rgba(99,102,241,0.06)";
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLTableRowElement).style.background =
                                isEven ? "rgba(255,255,255,0.01)" : "transparent";
                            }}
                          >
                            {/* # */}
                            <td className="px-3 sm:px-4 py-3 text-xs text-slate-600 tabular-nums font-medium">
                              {currentPage * pageSize + index + 1}
                            </td>
                            {/* Device */}
                            <td className="px-3 sm:px-4 py-3">
                              <span className="font-mono text-xs text-slate-300">
                                {item?.deviceId}
                              </span>
                            </td>
                            {/* Date */}
                            <td className="px-3 sm:px-4 py-3 text-xs text-slate-400 tabular-nums whitespace-nowrap">
                              {item?.timestamp?.split(/[T ]/)[0]}
                            </td>
                            {/* Time */}
                            <td className="px-3 sm:px-4 py-3 text-xs text-slate-400 tabular-nums whitespace-nowrap font-mono">
                              {item?.timestamp?.split(/[T ]/)[1]?.split(".")[0]}
                            </td>
                            {/* Resistance */}
                            <td className="px-3 sm:px-4 py-3">
                              <span
                                className="font-mono text-xs font-bold tabular-nums"
                                style={{
                                  color:
                                    status === "Critical"
                                      ? "#fb7185"
                                      : status === "Inactive"
                                      ? "#64748b"
                                      : "#fbbf24",
                                }}
                              >
                                {formatResistance(item?.R ?? item?.r ?? item?.resistance, false)}
                              </span>
                            </td>
                            {/* Voltage */}
                            <td className="px-3 sm:px-4 py-3">
                              <span className="font-mono text-xs text-blue-400 tabular-nums">
                                {item?.V?.toFixed(2) ?? "—"}
                              </span>
                            </td>
                            {/* Current */}
                            <td className="px-3 sm:px-4 py-3">
                              <span className="font-mono text-xs text-emerald-400 tabular-nums">
                                {item?.I?.toFixed(2) ?? "—"}
                              </span>
                            </td>
                            {/* Status */}
                            <td className="px-3 sm:px-4 py-3">
                              <StatusBadge status={status} />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Bottom pagination bar (mobile-friendly shortcut) */}
              <div
                className="flex items-center justify-between px-4 sm:px-6 py-3 flex-shrink-0 sm:hidden"
                style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
              >
                <button
                  onClick={() => setCurrentPage((p) => (p > 0 ? p - 1 : 0))}
                  disabled={reportApiData?.data?.first}
                  className="flex items-center gap-1.5 text-xs font-medium text-slate-400 disabled:opacity-30 cursor-pointer"
                >
                  <FiChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="text-xs text-indigo-300 font-semibold tabular-nums">
                  {currentPage + 1} of {reportApiData?.data?.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={reportApiData?.data?.last}
                  className="flex items-center gap-1.5 text-xs font-medium text-slate-400 disabled:opacity-30 cursor-pointer"
                >
                  Next <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Report;
